import * as Immutable from "immutable";
import * as React from "react";
import * as ReactDOM from "react-dom";
import StartAudioContext from "startaudiocontext";
import * as Tone from "tone";
import {
  ABC_OFF,
  AbcNote,
  createAbcFromData,
  createABCFromGrooveData,
  HHAbcSymbol,
  KickAbcSymbol,
  SnareAbcSymbol,
  StickAbcSymbol,
  TomAbcSymbol,
} from "./abc";
import { AbcSvg } from "./abcSvg";
import {
  changeDivision,
  changeTimeSignature,
  DrumData,
  Instrument,
  Note,
  structureData,
} from "./data";
import { getGrooveDataFromUrlString } from "./grooveUtils";
import "./index.css";
import { loadDrums } from "./instrument";
import TimeSignature from "./timeSignature";
import { isTripletDivision } from "./utils";

(window as any).Tone = Tone;

Tone.context.latencyHint = "fastest";

interface NoteProps {
  children: Note;
  onClick(): void;
}

function noteSymbol(note: AbcNote): string {
  switch (note) {
    case HHAbcSymbol.Accent:
      return ">";
    case HHAbcSymbol.Close:
      return "☰";
    case HHAbcSymbol.Cow_Bell:
      return "▢";
    case HHAbcSymbol.Crash:
      return "✲";
    case HHAbcSymbol.Normal:
      return "×";
    case HHAbcSymbol.Open:
      return "⊗";
    case HHAbcSymbol.Ride:
      return "⊙";
    case HHAbcSymbol.Ride_Bell:
      return "🔔";
    case KickAbcSymbol.Normal:
      return "⚫";
    case KickAbcSymbol.SandK:
      return "⊗";
    case KickAbcSymbol.Splash:
      return "×";
    case SnareAbcSymbol.Accent:
      return "›";
    case SnareAbcSymbol.Buzz:
      return "☰";
    case SnareAbcSymbol.Drag:
      return "_";
    case SnareAbcSymbol.Flam:
      return "🔥";
    case SnareAbcSymbol.Ghost:
      return "�";
    case SnareAbcSymbol.Normal:
      return "⚫";
    case SnareAbcSymbol.XStick:
      return "×";
    case StickAbcSymbol.COUNT:
      return "&";
    case StickAbcSymbol.L:
      return "L";
    case StickAbcSymbol.OFF:
      return "e";
    case StickAbcSymbol.R:
      return "R";
    case StickAbcSymbol.BOTH:
      return "R/L";
    case TomAbcSymbol.T1:
    case TomAbcSymbol.T2:
    case TomAbcSymbol.T3:
    case TomAbcSymbol.T4:
      return "⊗";
    default:
      console.warn(`unexpected note: ${note}`);
    case false:
      return "\xa0"; // nbsp
  }
}

function noteToToneNote(note: AbcNote): string | undefined {
  switch (note) {
    case HHAbcSymbol.Accent:
      return "C8";
    case HHAbcSymbol.Close:
      console.warn("not implemented");
      return undefined;
    case HHAbcSymbol.Cow_Bell:
      return "A7";
    case HHAbcSymbol.Crash:
      return "Db3";
    case HHAbcSymbol.Normal:
      return "Gb2";
    case HHAbcSymbol.Open:
      return "Bb2";
    case HHAbcSymbol.Ride:
      return "Eb3";
    case HHAbcSymbol.Ride_Bell:
      return "F3";
    case KickAbcSymbol.Normal:
      return "B1";
    case KickAbcSymbol.SandK:
    case KickAbcSymbol.Splash:
      console.warn("not implemented");
      return undefined;
    case SnareAbcSymbol.Accent:
      return "Bb0";
    case SnareAbcSymbol.Buzz:
      return "Ab7";
    case SnareAbcSymbol.Drag:
    case SnareAbcSymbol.Flam:
      return "B7";
    case SnareAbcSymbol.Ghost:
      return "A0";
    case SnareAbcSymbol.Normal:
      return "D2";
    case SnareAbcSymbol.XStick:
      return "Db2";
    case StickAbcSymbol.COUNT:
    case StickAbcSymbol.L:
    case StickAbcSymbol.OFF:
    case StickAbcSymbol.R:
    case StickAbcSymbol.BOTH:
    case TomAbcSymbol.T1:
    case TomAbcSymbol.T2:
    case TomAbcSymbol.T3:
    case TomAbcSymbol.T4:
      console.warn("not implemented");
      return undefined;
    default:
      console.warn(`unexpected note: ${note}`);
    case false:
      return undefined;
  }
}

function Note({ children, onClick }: NoteProps) {
  return (
    <button type="button" className="note-button" onClick={onClick}>
      {/* {noteSymbol(children)} */}
      {children}
    </button>
  );
}

interface AppState {
  instrument: null | Tone.Instrument;
}

class App extends React.Component<{}, AppState> {
  state: AppState = {
    instrument: null,
  };

  async componentWillMount() {
    console.log("ready?");
    const drums = await loadDrums();
    drums.toMaster();
    console.log("set?");
    // await Tone.loaded();
    console.log("go!");
    this.setState({ instrument: drums });
  }

  render() {
    return this.state.instrument ? (
      <Ui instrument={this.state.instrument} />
    ) : (
      <p>Loading...</p>
    );
  }
}

interface UiProps {
  instrument: Tone.Instrument;
}

interface UiState {
  grooveData: DrumData;
}

class Ui extends React.Component<UiProps, UiState> {
  state: UiState = {
    grooveData: new DrumData({}),
  };

  componentWillMount() {
    const drums = this.props.instrument;

    this._mainLoop = new Tone.Loop(time => {
      const noteIndex = this._mainLoopIndex;

      this.state.grooveData.instruments.forEach((notes, instrument) => {
        const noteName = notes!.get(noteIndex);
        this._playNote(instrument!, noteName);
      });

      if (noteIndex === this.state.grooveData.instruments.first().size - 1) {
        this._mainLoopIndex = 0;
      } else {
        this._mainLoopIndex++;
      }

      // schedule a little late to sync better visually
      this._highlightSchedule = Tone.Draw.schedule(
        () => this._setHighlightedNote(noteIndex),
        time,
      );
    }, `${this.state.grooveData.timeDivision}n`);
    this._mainLoop.start();

    Tone.Transport.bpm.value = 80;
    Tone.Transport.timeSignature = [
      this.state.grooveData.numBeats,
      this.state.grooveData.noteValue,
    ];
    Tone.Transport.swing = 0;

    this._measureMetronome = new Tone.Loop(time => {
      drums.triggerAttack("E5", time);
    }, "1m");
    this._measureMetronome.start();

    this._noteMetronome = new Tone.Loop(time => {
      drums.triggerAttack("F5", time);
    }, "8n");
    this._noteMetronome.start();
  }

  componentDidMount() {
    // TODO: don't let play be pressed until this resolves
    void StartAudioContext(Tone.context, this._playEl!);
  }

  componentWillUnmount() {
    this._mainLoop.dispose();
    this._measureMetronome.dispose();
    this._noteMetronome.dispose();
  }

  render() {
    try {
      const { grooveData } = this.state;
      const abc = createAbcFromData(grooveData, screen.width - 100);

      // triplets: 1 / (timeDivision * (2/3)) === notes
      // not triplets: 1 / timeDivision === notes

      // pre split everything up into an array with the indexes of each note
      // split by measure, then by note group
      const structuredData = structureData(grooveData);

      const canPlay =
        Tone.Transport.state === "stopped" || Tone.Transport.state === "paused";

      return (
        <div className="interface">
          <textarea onChange={this._handleChange} value="" />
          <hr />
          <div>
            <button
              type="button"
              ref={this._playRef}
              onClick={canPlay ? this._start : this._pause}
            >
              {canPlay ? "play" : "pause"}
            </button>
            <button type="button" onClick={this._stop}>
              stop
            </button>
            <button type="button" onClick={this._restart}>
              restart
            </button>
          </div>
          <div>
            <input
              type="checkbox"
              name="metronome"
              checked={!this._measureMetronome.mute}
              onChange={this._handleMetronomeToggle}
            />
            <select
              value={this._noteMetronome.interval}
              name="minormetronome"
              onChange={this._handleMetronomeSubChange}
            >
              {["4n", "4t", "8n", "8t", "16n", "16t"].map(v => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
            {Math.round(Tone.Transport.bpm.value)}bpm{" "}
            <input
              type="range"
              onChange={this._handleChangeBpm}
              step={1}
              min={30}
              max={300}
              value={Tone.Transport.bpm.value}
            />
            {Math.round(Tone.Transport.swing * 100)}% swing{" "}
            <input
              type="range"
              onChange={this._handleChangeSwing}
              step={1}
              min={0}
              max={100}
              value={Tone.Transport.swing * 100}
            />
          </div>
          <TimeSignature
            numBeats={this.state.grooveData.numBeats}
            noteValue={this.state.grooveData.noteValue}
            onChange={this._handleChangeTimeSignature}
          />
          <div>
            {[8, 12, 16, 24, 32, 48].map(division => (
              <button
                type="button"
                key={division}
                style={{
                  backgroundColor:
                    division === grooveData.timeDivision ? "yellow" : undefined,
                }}
                onClick={this._changeDivision(division)}
              >
                {division === 48
                  ? "mixed division"
                  : isTripletDivision(division)
                    ? `1/${division * (2 / 3)} triplets`
                    : `1/${division} notes`}
              </button>
            ))}
          </div>
          <div className="notes">
            <div className="instruments">
              {grooveData.instruments
                .map((notes, instrument) => (
                  <div key={instrument!.name}>
                    {instrument!.name}
                    <button disabled type="button">
                      🔇
                    </button>
                    <button disabled type="button">
                      📣
                    </button>
                  </div>
                ))
                .toArray()}
            </div>
            {structuredData.map((measure, measureI) => {
              measure = measure!;
              measureI = measureI!;
              return (
                <Measure
                  key={measureI}
                  measure={measure}
                  barRef={this._barRef}
                  makeHandleNoteClick={this._makeHandleNoteClick}
                  addMeasure={this._addMeasure(measureI)}
                  deleteMeasure={this._deleteMeasure(measureI)}
                />
              );
            })}
          </div>
          <AbcSvg abc={abc} ref={this._abcSvgRef} />
          {abc && (
            <pre
              style={{
                border: "1px solid green",
                overflowX: "auto",
                // maxHeight: 100,
              }}
            >
              {abc}
            </pre>
          )}
        </div>
      );
    } catch (e) {
      console.error(e);
      return <p style={{ color: "red" }}>ERROR</p>;
    }
  }

  private _mainLoop: Tone.Loop;
  private _mainLoopIndex: number = 0;
  private _measureMetronome: Tone.Loop;
  private _noteMetronome: Tone.Loop;
  private _highlightSchedule: Tone.DrawCls | undefined;

  private _noteEls: { [key: number]: HTMLElement } = {};
  private _highlightedNote: number | null = null;
  private _playEl: HTMLButtonElement | null = null;
  private _abcSvgEl: AbcSvg | null = null;

  private _playRef = (el: HTMLButtonElement | null) => {
    this._playEl = el;
  };

  private _abcSvgRef = (el: AbcSvg | null) => {
    this._abcSvgEl = el;
  };

  private _handleChange = (ev: React.ChangeEvent<HTMLTextAreaElement>) => {
    const grooveData = getGrooveDataFromUrlString(
      ev.target.value.split("?")[1] || ev.target.value,
    );
    this.setState({ grooveData });
  };

  private _changeDivision(division: number) {
    return () => {
      this.setState(
        state => ({
          grooveData: changeDivision(state.grooveData, division),
        }),
        () => {
          this._mainLoop.interval = `${division}n`;

          // TODO, switch to an appropriate metronome subdivision
          // const notationMatch = Tone.Time(this._noteMetronome.interval)
          //   .toNotation()
          //   .match(/(\d+)(\w+)$/);
          // if (notationMatch) {
          //   const [, num, w] = notationMatch;
          //   if (w === "t") {
          //     this._noteMetronome.interval = `${num}n`;
          //   }
          // }

          // TODO, I'd like this to maintain the existing position, but I'll restart it for now
          this._restart();
        },
      );
    };
  }

  private _handleChangeTimeSignature = (timeSignature: [number, number]) => {
    this.setState(
      state => ({
        grooveData: changeTimeSignature(state.grooveData, timeSignature),
      }),
      () => {
        Tone.Transport.timeSignature = timeSignature;

        // TODO, I'd like this to maintain the existing position, but I'll restart it for now
        this._restart();

        const oldMeasureCallback = this._measureMetronome.callback;
        this._measureMetronome.dispose();
        this._measureMetronome = new Tone.Loop(oldMeasureCallback, "1m");
        this._measureMetronome.start();

        const oldNoteCallback = this._noteMetronome.callback;
        const oldNoteInterval = this._noteMetronome.interval;
        this._noteMetronome.dispose();
        this._noteMetronome = new Tone.Loop(oldNoteCallback, oldNoteInterval);
        this._noteMetronome.start();
      },
    );
  };

  private _addMeasure(measureI: number) {
    return () => {
      this.setState(state => {
        const notesPerMeasure = state.grooveData.notesPerMeasure;
        const sliceBegin = measureI * notesPerMeasure;
        const sliceEnd = (measureI + 1) * notesPerMeasure;
        return {
          grooveData: state.grooveData.with({
            instruments: state.grooveData.instruments.map(notes => {
              notes = notes!;
              const begin = notes.take(sliceBegin);
              const mid = notes.slice(sliceBegin, sliceEnd);
              const end = notes.takeLast(notes.size - sliceBegin);
              return begin.concat(mid, end);
            }) as Immutable.OrderedMap<Instrument, Immutable.List<Note>>,
          }),
        };
      });
    };
  }

  private _deleteMeasure(measureI: number) {
    return () => {
      this.setState(state => {
        if (this._mainLoopIndex >= state.grooveData.instruments.first().size) {
          this._restart();
        }
        const notesPerMeasure = state.grooveData.notesPerMeasure;
        const sliceBegin = measureI * notesPerMeasure;
        const sliceEnd = (measureI + 1) * notesPerMeasure;
        return {
          grooveData: state.grooveData.with({
            instruments: state.grooveData.instruments.map(notes => {
              notes = notes!;
              const begin = notes.take(sliceBegin);
              const end = notes.takeLast(notes.size - sliceEnd);
              const result = begin.concat(end);
              if (result.size === 0) {
                return Immutable.List.of(
                  ...new Array(notesPerMeasure).fill(ABC_OFF),
                );
              }
              return result;
            }) as Immutable.OrderedMap<Instrument, Immutable.List<Note>>,
          }),
        };
      });
    };
  }

  private _handleChangeBpm = (ev: React.ChangeEvent<HTMLInputElement>) => {
    const bpm = parseInt(ev.target.value, 10);
    Tone.Transport.bpm.value = bpm;
    this.forceUpdate();
  };

  private _handleMetronomeToggle = (
    ev: React.ChangeEvent<HTMLInputElement>,
  ) => {
    this._measureMetronome.mute = !ev.target.checked;
    this._noteMetronome.mute = !ev.target.checked;
    this.forceUpdate();
  };

  private _handleMetronomeSubChange = (
    ev: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    this._noteMetronome.interval = ev.target.value;
    this.forceUpdate();
  };

  private _start = () => {
    const after = "+0.1";
    Tone.Transport.start(after);
    Tone.Transport.scheduleOnce(
      time => Tone.Draw.schedule(() => this.forceUpdate(), time),
      after,
    );
  };

  private _pause = () => {
    Tone.Transport.pause();
    this.forceUpdate();
  };

  private _restart = () => {
    Tone.Transport.seconds = 0;
    this._setHighlightedNote(null);
    this._mainLoopIndex = 0;
    this.forceUpdate();
  };

  private _stop = () => {
    Tone.Transport.stop();
    this._setHighlightedNote(null);
    this._mainLoopIndex = 0;
    this.forceUpdate();
  };

  private _handleChangeSwing = (ev: React.ChangeEvent<HTMLInputElement>) => {
    const swing = parseInt(ev.target.value, 10) / 100;
    Tone.Transport.swing = swing;
    this.forceUpdate();
  };

  private _barRef = (noteI: number) => (el: HTMLElement | null) => {
    if (el === null) {
      delete this._noteEls[noteI];
    } else {
      this._noteEls[noteI] = el;
    }
  };

  private _setHighlightedNote(note: number | null) {
    if (this._highlightSchedule) {
      this._highlightSchedule.cancel();
    }
    if (this._highlightedNote !== null) {
      this._noteEls[this._highlightedNote].dataset.playing = "";
    }
    if (note !== null) {
      this._noteEls[note].dataset.playing = "true";
    }
    if (this._abcSvgEl) {
      this._abcSvgEl.highlightNote(note);
    }
    this._highlightedNote = note;
  }

  private _playNote(instrument: Instrument, noteName: Note) {
    if (noteName) {
      const metadata = instrument.metadata.find(n => n.name === noteName);
      if (metadata) {
        const drums = this.props.instrument;
        drums.triggerAttack(metadata.note);
      } else {
        console.warn("didn't find metadata", instrument, noteName);
      }
    }
  }

  private _makeHandleNoteClick = (
    instrument: Instrument,
    noteI: number,
  ) => () => {
    this.setState(state => {
      const instruments = state.grooveData.instruments;
      const notes = instruments.get(instrument)!;
      const existingNoteName = notes.get(noteI);
      const newNoteName = existingNoteName ? null : instrument.metadata[0].name;
      this._playNote(instrument, newNoteName);
      return {
        grooveData: state.grooveData.with({
          instruments: instruments.set(
            instrument,
            notes.set(noteI, newNoteName),
          ),
        }),
      };
    });
  };
}

interface MeasureProps {
  measure: Immutable.Iterable<
    number,
    Immutable.Iterable<
      number,
      {
        i: number;
        notes: Immutable.Iterable<Instrument, Note>;
      }
    >
  >;
  barRef(noteI: number): (el: HTMLElement | null) => void;
  makeHandleNoteClick(instrument: Instrument, noteI: number): () => void;
  addMeasure(): void;
  deleteMeasure(): void;
}

class Measure extends React.Component<MeasureProps> {
  render() {
    const {
      barRef,
      makeHandleNoteClick,
      measure,
      addMeasure,
      deleteMeasure,
    } = this.props;
    return (
      <div className="measure">
        {measure
          .map((noteGroup, noteGroupI) => (
            <div key={noteGroupI} className="note-group">
              {noteGroup!
                .map((bar, barI) => {
                  bar = bar!;
                  barI = barI!;
                  return (
                    <div key={barI} className="bar" ref={barRef(bar.i)}>
                      {bar.notes
                        .map((note, instrument) => {
                          bar = bar!;
                          instrument = instrument!;
                          note = note!;
                          return (
                            <Note
                              key={instrument.name}
                              onClick={makeHandleNoteClick(instrument, bar.i)}
                            >
                              {note}
                            </Note>
                          );
                        })
                        .toArray()}
                    </div>
                  );
                })
                .toArray()}
            </div>
          ))
          .toArray()}
        <button type="button" onClick={addMeasure}>
          add measure
        </button>
        <button type="button" onClick={deleteMeasure}>
          delete measure
        </button>
      </div>
    );
  }
}

ReactDOM.render(<App />, document.getElementById("root"));



// WEBPACK FOOTER //
// ./src/index.tsx